<script lang="ts" setup>
import "@/utils/disposablePolyfill.ts";

import * as healpixGeo from "healpix-geo";
import { storeToRefs } from "pinia";
import * as THREE from "three";
import { onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import * as zarr from "zarrita";

import {
  useGridHoverLookup,
  type TGridHoverLookupResult,
} from "./composables/gridHoverUtils.ts";
import {
  faceValue,
  useAncestorFill,
  type TBeneath,
  type TReadLevel,
} from "./composables/useAncestorFill.ts";
import { useDigestField } from "./composables/useDigestField.ts";
import { useDigestProbe } from "./composables/useDigestProbe.ts";
import { useGapFill } from "./composables/useGapFill.ts";
import { useGridDataLoader } from "./composables/useGridDataLoader.ts";
import { usePointLayer } from "./composables/usePointLayer.ts";
import { useScalarFieldCache } from "./composables/useScalarFieldCache.ts";
import { useSharedGridLogic } from "./composables/useSharedGridLogic.ts";
import { useStreamlineLayer } from "./composables/useStreamlineLayer.ts";
import { useVolume } from "./composables/useVolume.ts";

import { buildDimensionRangesAndIndices } from "@/lib/data/dimensionHandling.ts";
import { coveredBlocks, hiveNodes } from "@/lib/data/hiveStore.ts";
import { parseStorePath } from "@/lib/data/icechunkStore.ts";
import {
  coarsestLevel,
  currentLevel,
  DEFAULT_MAX_LEVEL_CELLS,
  DEFAULT_VIEW_BUDGET_BYTES,
  DEFAULT_VIEW_LOAD_CELLS,
  STREAMLINES_NEED_WHOLE_LEVEL,
  windowBlocks,
} from "@/lib/data/levels.ts";
import { loadVectorComponents } from "@/lib/data/streamlineData.ts";
import {
  castDataVarToFloat32,
  getFillValue,
  getMissingValue,
} from "@/lib/data/variableDecoding.ts";
import {
  RegularVectorField,
  resolveVectorVariablePair,
} from "@/lib/data/vectorField.ts";
import {
  createVectorMagnitudeData,
  type TVectorMagnitudeData,
} from "@/lib/data/vectorMagnitude.ts";
import type { TAxisRange } from "@/lib/data/viewWindow.ts";
import { ZarrDataManager } from "@/lib/data/ZarrDataManager.ts";
import { digestVariableOf } from "@/lib/digest/digestVariables.ts";
import { strataBytesFactor } from "@/lib/digest/strata.ts";
import {
  getGridVariableData,
  terminateGridDataWorker,
} from "@/lib/grids/gridDataWorkerClient.ts";
import {
  createWindowRead,
  fetchInOrder,
  rangeDistance,
  orderedBlockRanges,
  readFaceProgressively,
  readRanges,
  type TFaceParts,
  type TRangeCells,
  type TWindowRead,
} from "@/lib/grids/healpixBlockFetch.ts";
import {
  HEALPIX_NUMCHUNKS,
  buildHealpixRegionCoordinates,
  buildHealpixTexture,
  getHealpixFaceDataRect,
  getHealpixFaceRange,
} from "@/lib/grids/healpixCalculations.ts";
import {
  cutWindowStale,
  healpixBlockDepth,
  healpixBlockLevel,
  healpixViewWindow,
  viewWindowMargin,
} from "@/lib/grids/healpixWindow.ts";
import {
  buildHealpixFace,
  terminateHealpixWorker,
} from "@/lib/grids/healpixWorkerClient.ts";
import type { THealpixBatch } from "@/lib/grids/healpixWorkerProtocol.ts";
import { createSortedCells } from "@/lib/grids/sortedCells.ts";
import {
  MORTON_DGGS,
  MORTON_STORE_ELLIPSOID,
} from "@/lib/morton/convention.ts";
import {
  createTriangleWrapProjectionGeometry,
  createWrappedProjectionMesh,
  setupProjectionGeometryWrap,
  updateProjectionMeshes,
} from "@/lib/projection/projectionEdgeQuality.ts";
import { ProjectionHelper } from "@/lib/projection/projectionUtils.ts";
import {
  getColormapScaleOffset,
  makeGpuProjectedTextureMaterial,
} from "@/lib/shaders/gridShaders.ts";
import type { TDimensionRange, TSources } from "@/lib/types/GlobeTypes.ts";
import { VOLUME_GRID_TYPES } from "@/lib/volume/volumeGrid.ts";
import { useUrlParameterStore } from "@/store/paramStore.ts";
import {
  HOVERED_GRID_POINT_STATUS,
  useGlobeControlStore,
} from "@/store/store.ts";
import { useLog } from "@/ui/common/useLog.ts";
import type { THistogramSummary } from "@/utils/histogram.ts";

const props = defineProps<{
  datasources?: TSources;
}>();

// By convention, HEALPIX uses -1.6375e+30 to mark invalid or unseen pixels.
const HEALPIX_UNSEEN = new Float32Array([-1.6375e30])[0];

function getHealpixMissingAndFillValues(
  datavar: zarr.Array<zarr.DataType, zarr.AsyncReadable>
) {
  const missingValue = getMissingValue(datavar);
  const fillValue = getFillValue(datavar);
  if (Number.isNaN(missingValue)) {
    return { missingValue: HEALPIX_UNSEEN, fillValue };
  }
  if (Number.isNaN(fillValue)) {
    return { missingValue, fillValue: HEALPIX_UNSEEN };
  }
  return { missingValue, fillValue };
}

const store = useGlobeControlStore();
const { logError } = useLog();
const { varnameSelector, colormap, invertColormap, dimSlidersValues, varinfo } =
  storeToRefs(store);

const urlParameterStore = useUrlParameterStore();
const { paramDimIndices, paramDimMinBounds, paramDimMaxBounds } =
  storeToRefs(urlParameterStore);

const {
  getScene,
  getRenderer,
  redraw,
  makeSnapshot,
  toggleRotate,
  applyCameraPreset,
  fitCameraToDataset,
  getDataVar,
  fetchDimensionDetails,
  updateLandSeaMask,
  updateColormap,
  updateHistogram,
  projectionHelper,
  isSceneInMotion,
  onProjectionChange,
  onMotionStateChange,
  onColormapChange,
  registerAnimationCallback,
  registerUpdateLOD,
  canvas,
  box,
  hoveredGeoPoint,
  clickedGeoPoint,
  pointPivot,
} = useSharedGridLogic();

const { setHoverLookup, clearHoverLookup } =
  useGridHoverLookup(hoveredGeoPoint);

const selectedDimensionNames = ref<string[]>([]);

const healpixGrid = ref<healpixGeo.Grid | null>(null);
const gridPrepared = ref<boolean>(false);

type TStreamlineContext = {
  indices: (number | null | zarr.Slice)[];
  grid: healpixGeo.Grid;
  cellCoord?: number[];
};

let lastStreamlineContext: TStreamlineContext | undefined;
let streamlineRequestRevision = 0;
let cachedMagnitude: TVectorMagnitudeData | undefined;
let cachedStreamlineKey: string | undefined;

let disposed = false;

// The level as stored: how many cells, and whether it can be loaded by view
// (nested, and either dense or with an ascending `cell` coordinate).
let levelCellCount = 0;
let levelWindowable = false;
// The orders between the level's cells and the blocks of its view window
// (`healpixBlockDepth`), and the cells of one of its chunks.
let blockDepth: number | undefined;
let levelChunkCells = Infinity;
// whether the variable is derived from a t-digest: its window is priced in
// bytes, not cells
let levelDigest = false;
// whether the budget left blocks out of the loaded window, and the point
// below the camera when it was loaded
let windowCut = false;
let windowCentre = { centreLon: 0, centreLat: 0 };
let sortedCells: ReturnType<typeof createSortedCells> | undefined;
// The coordinate reader of a sparse level, kept while the level stays the
// same so that its chunks are read once.
let cellsReader:
  { key: string; cells: ReturnType<typeof createSortedCells> } | undefined;
// The blocks loaded of a level too large to load whole; undefined when the
// whole level is loaded.
let loadedBlocks: number[] | undefined;
// The slice and the field key last loaded: what the next finer level is
// read ahead at, once the view rests (`useAncestorFill.prefetch`).
let lastField:
  { indices: (number | zarr.Slice | null)[]; key: string } | undefined;

/**
 * The ranges of a digest window nearest the camera read again once it has
 * landed, for the cursor's distribution there: one round of reads in flight.
 */
const CENTRE_RANGES = 8;

let mainMeshes: Array<THREE.Mesh | undefined> = new Array(HEALPIX_NUMCHUNKS);

// The coarsest level of a multi-resolution dataset at the selected slice,
// loaded whole: a finer level takes its colour range from it, so the range
// holds still, and it is the end of the ladder drawn beneath.
type TCoarsestFrame = {
  key: string;
  index: number;
  grid: healpixGeo.Grid;
  min: number;
  max: number;
  histogramSummaries: THistogramSummary[];
  batches: THealpixBatch[];
};
let coarsestFrame: TCoarsestFrame | undefined;
// The level drawn beneath the one on screen (`useAncestorFill`), by key.
let backdropKey: string | undefined;
const backdropMeshes: Array<THREE.Mesh | undefined> = new Array(
  HEALPIX_NUMCHUNKS
);

function drawnMeshes() {
  return [...backdropMeshes, ...mainMeshes];
}

/**
 * The level's colours, and whether it is drawn at all: under the point tier
 * the level and its backdrop are hidden (the cloud is drawn on black), which
 * `updateColormap` undoes before it redraws, so the meshes it is given are
 * only those to be shown.
 */
function updateLevelColormap() {
  const shown = pointLayer.active.value ? [] : drawnMeshes();
  for (const mesh of drawnMeshes()) {
    if (mesh && !shown.includes(mesh)) {
      mesh.visible = false;
    }
  }
  updateColormap(shown);
}

onColormapChange(updateLevelColormap);

onProjectionChange(updateMeshProjectionUniforms);
onMotionStateChange(updateMeshProjectionUniforms);

const scalarCache = useScalarFieldCache({
  updateHistogram,
  updateColormap: updateLevelColormap,
  redraw,
});

const streamlines = useStreamlineLayer({
  getScene,
  redraw,
  projectionHelper,
  onProjectionChange,
  registerAnimationCallback,
});

const volume = useVolume({
  getDatasources: () => props.datasources,
  getScene,
  getRenderer,
  redraw,
  projectionHelper,
  isSceneInMotion,
  onProjectionChange,
  onMotionStateChange,
});

/**
 * Update projection uniforms on all mesh materials.
 * This is the fast path - no geometry rebuild needed.
 */
function updateMeshProjectionUniforms() {
  updateProjectionMeshes(drawnMeshes(), {
    redraw,
    projectionHelper: projectionHelper.value,
    isSceneInMotion: isSceneInMotion.value,
  });
}

const { datasourceUpdate, getData } = useGridDataLoader({
  getDatasources: () => props.datasources,
  getDataVar,
  fetchAndRenderData,
  scalarCache,
  clearHoverLookup,
  prepareDatasource: prepareLevel,
  canLoadByView: () => levelWindowable,
  viewWindowStale,
  onIdle: (isCurrent) => {
    if (lastField) {
      void fill
        .prefetch(lastField.indices, lastField.key, isCurrent)
        .catch(() => undefined);
    }
  },
  updateLandSeaMask,
  updateColormap: updateLevelColormap,
  refreshStreamlines: async (reuseCached) => {
    if (lastStreamlineContext) {
      await updateStreamlines(lastStreamlineContext, reuseCached);
    }
  },
  suspendStreamlines: () => {
    streamlineRequestRevision++;
    store.streamlineLoading = false;
    store.streamlineProgress = undefined;
  },
});

// Variables derived from a t-digest are computed here, not read.
async function recompute() {
  await getData();
  updateLevelColormap();
}
const digestFields = useDigestField({
  getDatasources: () => props.datasources,
  reload: recompute,
});
const gapFill = useGapFill({
  getDatasources: () => props.datasources,
  reload: recompute,
});
useDigestProbe({
  getDatasources: () => props.datasources,
  getGrid: () => healpixGrid.value as healpixGeo.Grid | null,
  clickedGeoPoint,
  getInterpolated: gapFill.interpolated,
  filledRevision: gapFill.revision,
  getBeneath: () => fill.beneath(),
});
const fill = useAncestorFill({
  getDatasources: () => props.datasources,
  describe: async (sources) => {
    const datavar = await getDataVar(varnameSelector.value, sources);
    return (
      datavar && { datavar, grid: await getHealpixGridParameters(sources) }
    );
  },
  window: levelWindow,
  read: async (level, blocks, seed, key, indices, isCurrent) => {
    // the faces land in place of the level beneath before; a face with
    // nothing in the window goes as it is rebuilt
    backdropKey = key;
    drawOverBackdrop();
    const result = await processHealpixChunks(level, indices, isCurrent, {
      deferDisplay: false,
      window: { blocks, seed },
      target: backdropMeshes,
    });
    return result && { parts: result.parts, batches: result.drawn };
  },
  show: showBackdrop,
  meshes: backdropMeshes,
  ranges: windowRanges,
  fetchRange: fetchCellRange,
  byView: overWholeLevelCap,
});
// the point tier: the observations in place of the finest level (levels.ts)
const pointLayer = usePointLayer({
  getDatasources: () => props.datasources,
  getScene,
  redraw,
  registerUpdateLOD,
  onColormapChange,
  updateColormap,
  updateHistogram,
  restoreHistogram: scalarCache.restoreHistogram,
  projectionHelper,
  pointPivot,
});
// the level comes and goes with the tier; its data stays for the probe
watch(pointLayer.active, () => {
  updateLevelColormap();
  redraw();
});

function coerceInteger(value: unknown): number | null {
  const cast = typeof value === "number" ? value : Number(value);

  return Number.isInteger(cast) && cast > 0 ? cast : null;
}

function coerceOrder(value: unknown): healpixGeo.IndexingScheme | null {
  const order = value === undefined || value === null ? null : String(value);

  // translate, but let healpixGeo validate
  if (order === "nest") {
    return "nested" as healpixGeo.IndexingScheme;
  } else {
    return order as healpixGeo.IndexingScheme;
  }
}

function coerceScheme(value: unknown): healpixGeo.IndexingScheme | null {
  const scheme = value === undefined || value === null ? null : String(value);

  return scheme as healpixGeo.IndexingScheme;
}

function coerceEllipsoid(value: unknown): healpixGeo.EllipsoidInput | null {
  return value === undefined || value === null
    ? null
    : (value as healpixGeo.EllipsoidInput);
}

async function gridFromEasygemsConvention(
  sources: TSources
): Promise<healpixGeo.Grid | null> {
  try {
    const crs = await ZarrDataManager.getCRSInfo(
      sources,
      varnameSelector.value
    );
    const nside = coerceInteger(crs.attrs["healpix_nside"]);
    const scheme = coerceOrder(crs.attrs["healpix_order"]);

    if (nside !== null && scheme !== null) {
      return new healpixGeo.Grid({ scheme: scheme, level: Math.log2(nside) });
    }
    // CRS variable exists but has no usable nside or order
  } catch {
    // No CRS variable
  }

  // try the next convention
  return null;
}

async function gridFromDggsConvention(
  sources: TSources
): Promise<healpixGeo.Grid | null> {
  const metadata = await ZarrDataManager.getDggsMetadata(
    sources,
    varnameSelector.value
  );
  if (metadata !== null) {
    const level = coerceInteger(metadata["refinement_level"]);
    // A morton group is nested by construction and spells its ellipsoid in
    // its own keys; the stores pin WGS84 (see convention.ts).
    const morton = metadata["name"] === MORTON_DGGS;
    const scheme = morton
      ? "nested"
      : coerceScheme(metadata["indexing_scheme"]);
    const ellipsoid = morton
      ? MORTON_STORE_ELLIPSOID
      : coerceEllipsoid(metadata["ellipsoid"]);

    if (level !== null && scheme !== null) {
      // ellipsoid is optional
      return new healpixGeo.Grid({ scheme, level, ellipsoid });
    }
  }

  // try another convention
  return null;
}

/**
 * Derive nside from the length of the (last) cell dimension, assuming a global
 * grid where `ncells = 12 * nside^2`. Returns null unless that yields an exact
 * positive integer nside, so it never misfires on limited-area data.
 */
function inferGridFromCellCount(
  datavar: zarr.Array<zarr.DataType, zarr.AsyncReadable>
): healpixGeo.Grid | null {
  const ncells = datavar.shape[datavar.shape.length - 1];
  if (!ncells) {
    return null;
  }

  const nside = coerceInteger(Math.sqrt(ncells / 12));
  if (nside !== null) {
    const level = Math.log2(nside);
    return new healpixGeo.Grid({ scheme: "nested", level: level });
  }

  // try another convention
  return null;
}

async function getHealpixGridParameters(
  sources = props.datasources!
): Promise<healpixGeo.Grid> {
  const fromEasygems = await gridFromEasygemsConvention(sources);
  if (fromEasygems !== null) {
    return fromEasygems;
  }

  const fromDggsConvention = await gridFromDggsConvention(sources);
  if (fromDggsConvention !== null) {
    return fromDggsConvention;
  }

  // last resort: assume nested / spherical and infer level from a global grid's cell count (12 * 4^level)
  const datavar = await ZarrDataManager.getVariableInfo(
    ZarrDataManager.getDatasetSource(sources, varnameSelector.value),
    varnameSelector.value
  );
  const fromShape = await inferGridFromCellCount(datavar);
  if (fromShape !== null) {
    return fromShape;
  }

  throw new Error(
    "Could not determine HEALPix grid parameters: no valid convention metadata on the grid mapping variable" +
      " or in the group metadata (tried the Easygems and dggs zarr conventions), and " +
      "the cell-dimension length is not 12 * nside^2."
  );
}

function unpackGrid(): healpixGeo.Grid {
  const grid = healpixGrid.value;
  if (grid === null) {
    throw new Error("failed to fetch grid parameters");
  }
  return grid as healpixGeo.Grid;
}

async function getCellCoordinateName(sources = props.datasources!) {
  let cellCoord = "cell";
  const dggsMetadata = await ZarrDataManager.getDggsMetadata(
    sources,
    varnameSelector.value
  );
  if (dggsMetadata !== null) {
    const coordinate = dggsMetadata["coordinate"];
    if (coordinate) {
      cellCoord = coordinate;
    }
  } else {
    // no dggs metadata found, continue with the default cell coordinate
  }
  return cellCoord as string;
}

async function getCells(sources = props.datasources!) {
  const cellCoord = await getCellCoordinateName(sources);

  try {
    // A level with every cell of its order is read by index (see
    // `searchedCoordinate`): its coordinate, if it stores one, is not nested
    // ids (a zagg level's holds packed morton words and fill).
    const datavar = await ZarrDataManager.getVariableInfoByDatasetSources(
      sources,
      varnameSelector.value
    );
    const cellCount = datavar.shape[datavar.shape.length - 1];
    const nside = Math.sqrt(cellCount / 12);
    if (Number.isInteger(nside) && Number.isInteger(Math.log2(nside))) {
      return undefined;
    }
    const rawCells = await fetchHealpixVariableData(
      [],
      ZarrDataManager.resolveVariablePath(varnameSelector.value, cellCoord),
      sources
    );

    return Array.from(rawCells, (cell) => Number(cell));
  } catch {
    return undefined;
  }
}

/**
 * Whether the selected level is loaded by view: a large level other than the
 * coarsest, which is the backdrop of the others and is always loaded whole.
 * Streamlines and volumes read the whole level, up to the whole-level cap.
 * A level derived from the leaves is never loaded whole.
 */
function loadsByView() {
  const sources = props.datasources!;
  if (currentLevel(sources).derived) {
    return true;
  }
  return (
    levelWindowable &&
    (sources.selectedLevel ?? 0) !== coarsestLevel(sources.levels) &&
    overWholeLevelCap(levelCellCount)
  );
}

/**
 * Whether a level of `cellCount` cells is too large to load whole: past the
 * whole-level cap with streamlines or a volume, past the view cap without.
 */
function overWholeLevelCap(cellCount: number) {
  const wholeLevelLayers =
    store.isStreamlineLayerEnabled() || store.isVolumeLayerEnabled();
  return (
    cellCount >
    (wholeLevelLayers ? DEFAULT_MAX_LEVEL_CELLS : DEFAULT_VIEW_LOAD_CELLS)
  );
}

/**
 * The blocks of a level in view at a margin, within the budget: cells for a
 * stored variable, bytes for one derived from a t-digest. `clipped` says
 * whether the budget cut into the view itself.
 */
function levelWindow(
  level: Pick<TReadLevel, "sources" | "grid" | "blockDepth" | "digest">,
  margin: number
) {
  const { sources, grid, blockDepth: depth } = level;
  const blockLevel = healpixBlockLevel(grid.level, depth);
  const cellsPerBlock = 4 ** (grid.level - blockLevel);
  // a hive store says where it has nodes: the window reads nothing elsewhere
  const source = ZarrDataManager.getDatasetSource(
    sources,
    varnameSelector.value
  );
  const nodes =
    source && hiveNodes(parseStorePath(source.store).url, grid.level);
  const maxBlocks = windowBlocks(
    currentLevel(sources),
    grid.level,
    cellsPerBlock,
    level.digest,
    DEFAULT_VIEW_BUDGET_BYTES,
    strataBytesFactor(store.digestStratum)
  );
  return store.viewFootprint
    ? healpixViewWindow(
        grid,
        store.viewFootprint,
        margin,
        maxBlocks,
        depth,
        nodes && ((order) => coveredBlocks(nodes, order))
      )
    : { blocks: [], cut: false, clipped: false };
}

/** The window of the level on screen. */
function viewWindow(margin: number) {
  return levelWindow(
    {
      sources: props.datasources!,
      grid: unpackGrid(),
      blockDepth,
      digest: levelDigest,
    },
    margin
  );
}

/** The level on screen, as the window reads it. */
function readLevel(datavar: TReadLevel["datavar"]): TReadLevel {
  return {
    sources: props.datasources!,
    datavar,
    grid: unpackGrid(),
    blockDepth,
    chunkCells: levelChunkCells,
    digest: levelDigest,
    sortedCells,
  };
}

function viewWindowStale() {
  if (!loadsByView()) {
    return loadedBlocks !== undefined;
  }
  if (loadedBlocks === undefined) {
    return true;
  }
  const loaded = new Set(loadedBlocks);
  const missing = viewWindow(0).blocks.filter((block) => !loaded.has(block));
  if (missing.length === 0) {
    return false;
  }
  return windowCut
    ? cutWindowStale(
        unpackGrid(),
        loadedBlocks,
        missing,
        store.viewFootprint!,
        windowCentre,
        blockDepth
      )
    : true;
}

/**
 * The path of a level's cell coordinate and, when the level is not dense,
 * the coordinate itself: a level with every cell of its order is read by
 * index, and its coordinate, if it stores one, is never bisected (a zagg
 * level's holds packed morton words, not nested ids).
 */
async function searchedCoordinate(
  sources: TSources,
  variable: string,
  grid: healpixGeo.Grid,
  cellCount: number
) {
  const cellPath = ZarrDataManager.resolveVariablePath(
    variable,
    await getCellCoordinateName(sources)
  );
  const coordinate =
    cellCount === 12 * grid.nside ** 2
      ? undefined
      : await ZarrDataManager.getVariableInfoByDatasetSources(
          sources,
          cellPath
        ).catch(() => undefined);
  return { cellPath, coordinate };
}

/**
 * Read what the stored level allows: its grid, its size, and whether it can
 * be loaded by view (nested, in a pyramid with a coarsest level to take the
 * range from, and either dense or with an ascending `cell` coordinate).
 * Then switch to it and to the blocks in view in one step.
 */
async function prepareLevel() {
  const sources = props.datasources!;
  const variable = varnameSelector.value;
  const grid = await getHealpixGridParameters();
  const datavar = await ZarrDataManager.getVariableInfoByDatasetSources(
    sources,
    variable
  );
  const cellCount = datavar.shape[datavar.shape.length - 1];
  let windowable = false;
  let cells: ReturnType<typeof createSortedCells> | undefined;
  if (coarsestLevel(sources.levels) !== undefined && grid.scheme === "nested") {
    const { cellPath, coordinate } = await searchedCoordinate(
      sources,
      variable,
      grid,
      cellCount
    );
    if (coordinate) {
      const key = `${sources.selectedLevel ?? 0}:${cellPath}`;
      if (cellsReader?.key !== key) {
        // the reader keeps reading this level, whatever is selected later
        const level = { ...sources };
        cellsReader = {
          key,
          cells: createSortedCells(
            coordinate.shape[0],
            coordinate.chunks[0],
            async (start, end) =>
              (await fetchHealpixVariableData(
                [zarr.slice(start, end)],
                cellPath,
                level
              )) as ArrayLike<number | bigint>
          ),
        };
      }
      windowable = await cellsReader.cells.isAscending();
      cells = windowable ? cellsReader.cells : undefined;
    } else {
      windowable = cellCount === 12 * grid.nside ** 2;
    }
  }
  // Nothing below waits: a view change in between never sees half of two
  // levels.
  healpixGrid.value = grid;
  levelCellCount = cellCount;
  levelWindowable = windowable;
  sortedCells = cells;
  prepareWindow(datavar, sources);
}

/** Switch to the blocks in view of the level just prepared, if it is windowed. */
function prepareWindow(
  datavar: zarr.Array<zarr.DataType, zarr.AsyncReadable>,
  sources: TSources
) {
  levelChunkCells = datavar.chunks.at(-1)!;
  blockDepth = healpixBlockDepth(
    levelChunkCells,
    currentLevel(sources).derived !== undefined
  );
  levelDigest = digestVariableOf(datavar.attrs) !== undefined;
  const window = loadsByView()
    ? viewWindow(viewWindowMargin(datavar.attrs))
    : undefined;
  loadedBlocks = window?.blocks;
  windowCut = window?.cut ?? false;
  const { centreLon = 0, centreLat = 0 } = store.viewFootprint ?? {};
  windowCentre = { centreLon, centreLat };
  store.setViewWindowClipped(window?.clipped ?? false);
}

/** The stored cells of a level with ids in a nested range, and their values. */
async function fetchCellRange(
  level: TReadLevel,
  range: { start: number; end: number },
  indices: (number | zarr.Slice | null)[],
  percentile?: number
) {
  const { sortedCells: sorted } = level;
  // Dense levels store cell `n` at index `n`; sparse ones are searched.
  const start = sorted ? await sorted.lowerBound(range.start) : range.start;
  const end = sorted ? await sorted.lowerBound(range.end) : range.end;
  if (start === end) {
    return { data: new Float32Array(), cells: [] as number[] };
  }
  const selection = indices.slice();
  selection[selection.length - 1] = zarr.slice(start, end);
  // the ids and the values are fetched together
  const [cells, values] = await Promise.all([
    sorted
      ? sorted.slice(start, end)
      : Array.from({ length: end - start }, (_, index) => start + index),
    fetchHealpixVariableData(selection, undefined, level.sources, percentile),
  ]);
  if (cells[0] < range.start || cells[cells.length - 1] >= range.end) {
    throw new Error("The cell coordinate is not in ascending order.");
  }
  return { data: castDataVarToFloat32(values), cells };
}

/** The cell ranges of a level's blocks, those under the camera first. */
function windowRanges(level: TReadLevel, blocks: number[]) {
  const { grid } = level;
  const blockLevel = healpixBlockLevel(grid.level, level.blockDepth);
  using blockGrid = grid.replace({ level: blockLevel, scheme: "nested" });
  const centre = store.viewFootprint ?? { centreLon: 0, centreLat: 0 };
  return orderedBlockRanges(
    blockGrid,
    blocks,
    4 ** (grid.level - blockLevel),
    level.chunkCells,
    centre.centreLon,
    centre.centreLat
  );
}

function fetchHealpixVariableData(
  selection: (number | zarr.Slice | null)[],
  variable = varnameSelector.value,
  sources = props.datasources!,
  // the height at one percentile, whatever the digest variable shows
  percentile?: number
) {
  const derived = digestFields.fetch(selection, variable, sources, percentile);
  if (derived) {
    return derived;
  }
  return getGridVariableData({
    source: ZarrDataManager.getDatasetSource(sources, varnameSelector.value),
    variable,
    format: sources.zarr_format,
    selection,
  });
}

function createGeometry({
  positionValues,
  uv,
  latLonValues,
  indices,
}: THealpixBatch) {
  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positionValues, 3)
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  // Add latLon attribute for GPU projection
  geometry.setAttribute(
    "latLon",
    new THREE.Float32BufferAttribute(latLonValues, 2)
  );
  return createTriangleWrapProjectionGeometry(geometry);
}

function fitCameraToCells(grid: healpixGeo.Grid, cells: number[] | undefined) {
  if (!cells || disposed) {
    return;
  }
  const regions = [];
  for (let face = 0; face < HEALPIX_NUMCHUNKS; face++) {
    const rect = getHealpixFaceDataRect(face, grid.nside, cells);
    if (rect) {
      regions.push({
        geometry: new THREE.BufferGeometry().setAttribute(
          "latLon",
          new THREE.BufferAttribute(
            buildHealpixRegionCoordinates(grid, face, rect),
            2
          )
        ),
      });
    }
  }
  // Cell coordinates describe the entire cutout before any face data is loaded.
  fitCameraToDataset(regions);
}

async function showMagnitude(scalar: TVectorMagnitudeData) {
  const context = lastStreamlineContext;
  if (!context) {
    return;
  }
  await scalarCache.showMagnitude(scalar, () => {
    const { grid, cellCoord } = context;
    const textures: THealpixTexture[] = [];
    for (let faceIndex = 0; faceIndex < HEALPIX_NUMCHUNKS; faceIndex++) {
      const range = getHealpixFaceRange(faceIndex, grid.nside, cellCoord);
      if (range.start === range.end) {
        continue;
      }
      textures.push({
        batchIndex: faceIndex,
        ...buildHealpixTexture(
          scalar.data.subarray(range.start, range.end),
          faceIndex,
          grid.nside,
          range.cells
        ),
      });
    }
    return () => textures.forEach((texture) => updateHealpixTexture(texture));
  });
}

async function prepareDimensionData(
  datavar: zarr.Array<zarr.DataType, zarr.AsyncReadable>
) {
  const dimensionNames = await ZarrDataManager.getDimensionNames(
    props.datasources!,
    varnameSelector.value
  );
  selectedDimensionNames.value = dimensionNames;
  const { dimensionRanges, indices } = buildDimensionRangesAndIndices(
    datavar,
    dimensionNames,
    paramDimIndices.value,
    paramDimMinBounds.value,
    paramDimMaxBounds.value,
    dimSlidersValues.value.length > 0 ? dimSlidersValues.value : null,
    [datavar.shape.length - 1],
    varinfo.value?.dimRanges
  );

  return { dimensionRanges, indices };
}

function makeHealpixVectorField(
  grid: healpixGeo.Grid,
  cellCoord: number[] | undefined,
  uValues: Float32Array,
  vValues: Float32Array
) {
  const cellIndex = cellCoord
    ? new Map(cellCoord.map((pixel, index) => [pixel, index]))
    : undefined;

  const latitudes = new Float32Array(179);
  const longitudes = new Float32Array(360);

  const nCoords = latitudes.length * longitudes.length;
  const bytesPerElement = 8;
  const pageSize = 65536;
  const memory = new WebAssembly.Memory({
    initial: Math.ceil((2 * nCoords * bytesPerElement) / pageSize),
  });
  const coords = new Float64Array(memory.buffer);

  for (let index = 0; index < nCoords; index++) {
    let y = Math.floor(index / 360);
    let x = index % 360;

    const lon = x - 180;
    const lat = y - 89;

    coords[2 * index] = lon;
    coords[2 * index + 1] = lat;

    longitudes[x] = lon;
    latitudes[y] = lat;
  }

  const nestedGrid = grid.replace({ scheme: "nested" });
  let pixels = nestedGrid.lonLatToHealpix(coords);

  const uData = new Float32Array(nCoords);
  const vData = new Float32Array(nCoords);

  for (let outputIndex = 0; outputIndex < pixels.length; outputIndex++) {
    const pixel = Number(pixels[outputIndex]); // assume this never exceeds 2^53 - 1, which is true for level < 25
    const inputIndex = cellIndex ? cellIndex.get(pixel) : pixel;
    const u = inputIndex === undefined ? NaN : uValues[inputIndex];
    const v = inputIndex === undefined ? NaN : vValues[inputIndex];
    uData[outputIndex] = u === HEALPIX_UNSEEN ? NaN : u;
    vData[outputIndex] = v === HEALPIX_UNSEEN ? NaN : v;
  }

  return new RegularVectorField(latitudes, longitudes, uData, vData);
}

// eslint-disable-next-line max-lines-per-function
async function updateStreamlines(
  context: TStreamlineContext,
  reuseCached = false
) {
  const requestRevision = ++streamlineRequestRevision;
  const variableNames = Object.keys(
    (props.datasources && currentLevel(props.datasources)?.datasources) ?? {}
  );
  const pair = resolveVectorVariablePair(
    variableNames,
    varnameSelector.value,
    store.streamlineSelection,
    store.isStreamlineLayerEnabled() ? store.streamlinePair : undefined
  );
  if (!pair || !props.datasources) {
    cachedMagnitude = undefined;
    cachedStreamlineKey = undefined;
    store.setStreamlineMagnitudeInfo(undefined);
    streamlines.clear();
    return;
  }
  const requestKey = JSON.stringify({
    indices: context.indices,
    nside: context.grid.nside,
    cells: context.cellCoord
      ? [
          context.cellCoord.length,
          context.cellCoord[0],
          context.cellCoord.at(-1),
        ]
      : undefined,
    pair: [pair.u, pair.v],
    level: store.streamlineLevelIndex,
  });
  if (
    reuseCached &&
    requestKey === cachedStreamlineKey &&
    streamlines.showCached()
  ) {
    if (store.streamlineMagnitudeDisplayed && cachedMagnitude) {
      await showMagnitude(cachedMagnitude);
    }
    return;
  }
  const nside = context.grid.nside;

  if (!store.isStreamlineLayerEnabled()) {
    if (requestKey === cachedStreamlineKey) {
      store.setStreamlinePair(pair);
    } else {
      cachedMagnitude = undefined;
      cachedStreamlineKey = undefined;
      store.setStreamlineMagnitudeInfo(undefined);
      streamlines.setAvailablePair(pair);
    }
    return;
  }
  if (loadedBlocks) {
    // only a level picked by hand that is over the whole-level cap gets here
    cachedMagnitude = undefined;
    cachedStreamlineKey = undefined;
    store.setStreamlineMagnitudeInfo(undefined);
    streamlines.clear(STREAMLINES_NEED_WHOLE_LEVEL);
    return;
  }
  streamlines.startLoading();
  try {
    const expectedDataLength = context.cellCoord?.length ?? 12 * nside * nside;
    const components = await loadVectorComponents({
      pair,
      datasources: props.datasources,
      getDataVar,
      currentDimensionNames: selectedDimensionNames.value,
      currentIndices: context.indices,
      spatialDimensionNames: [selectedDimensionNames.value.at(-1)!],
      expectedDataLength,
      selectedLevelIndex: store.streamlineLevelIndex,
    });
    if (requestRevision !== streamlineRequestRevision) {
      return;
    }
    store.setStreamlineLevelInfo(components?.levelInfo);
    store.setStreamlineMagnitudeInfo(
      components?.magnitudeInfo,
      components?.canDeriveMagnitude
    );
    if (!components || components.incompatibility !== undefined) {
      cachedMagnitude = undefined;
      cachedStreamlineKey = undefined;
      streamlines.clear(components?.incompatibility);
      return;
    }
    if (
      !(await streamlines.prepareField(
        () => requestRevision === streamlineRequestRevision
      ))
    ) {
      return;
    }
    const magnitude =
      components.magnitudeInfo && components.canDeriveMagnitude
        ? createVectorMagnitudeData(
            components.uData,
            components.vData,
            components.magnitudeInfo
          )
        : undefined;
    const rendered = await streamlines.setField(
      makeHealpixVectorField(
        context.grid,
        context.cellCoord,
        components.uData,
        components.vData
      ),
      pair,
      () => requestRevision === streamlineRequestRevision,
      async () => {
        if (store.streamlineMagnitudeDisplayed && magnitude) {
          await showMagnitude(magnitude);
        } else {
          await scalarCache.restoreScalar();
        }
      }
    );
    if (!rendered || requestRevision !== streamlineRequestRevision) {
      return;
    }
    cachedMagnitude = magnitude;
    cachedStreamlineKey = requestKey;
  } catch (error) {
    if (requestRevision === streamlineRequestRevision) {
      streamlines.clear();
      logError(error, "Could not render vector streamlines");
    }
  }
}

async function getDimensionValues(
  dimensionRanges: TDimensionRange[],
  indices: (number | zarr.Slice | null)[]
) {
  const dimValues = await fetchDimensionDetails(
    varnameSelector.value,
    props.datasources!,
    dimensionRanges,
    indices
  );
  return dimValues;
}

function disposeHealpixMesh(batchIndex: number, target = mainMeshes) {
  const mesh = target[batchIndex];
  if (!mesh) {
    return;
  }
  mesh.geometry.dispose();
  const mat = mesh.material as THREE.ShaderMaterial;
  if (mat) {
    if (mat.uniforms?.data?.value?.dispose) {
      mat.uniforms.data.value.dispose();
    }
    mat.dispose();
  }
  getScene()?.remove(mesh);
  target[batchIndex] = undefined;
}

function createHealpixMesh(
  batchIndex: number,
  geometry: THREE.InstancedBufferGeometry,
  target = mainMeshes
) {
  const { addOffset, scaleFactor } = getColormapScaleOffset(
    store.selection?.low as number,
    store.selection?.high as number,
    invertColormap.value
  );
  const material = makeGpuProjectedTextureMaterial(
    new THREE.Texture(),
    colormap.value,
    addOffset,
    scaleFactor
  );
  material.uniforms.useTriangleWrapCull.value = 1;
  const mesh = createWrappedProjectionMesh(
    geometry,
    material,
    projectionHelper.value.type
  );
  mesh.frustumCulled = false;
  mesh.visible = !pointLayer.active.value;
  if (target === backdropMeshes) {
    // after the layers stacked below the grid (-1 and down), before the grid
    mesh.renderOrder = -0.5;
  }
  target[batchIndex] = mesh;
  getScene()?.add(mesh);
  return mesh;
}

function updateHealpixBatch(batch: THealpixBatch, target = mainMeshes) {
  if (batch.width === 0 || batch.height === 0) {
    // No cells of a regional/sparse dataset fall in this face.
    disposeHealpixMesh(batch.batchIndex, target);
    return;
  }
  const geometry = createGeometry(batch);
  let mesh = target[batch.batchIndex];
  if (mesh) {
    mesh.geometry.dispose();
    setupProjectionGeometryWrap(geometry);
    mesh.geometry = geometry;
  } else {
    mesh = createHealpixMesh(batch.batchIndex, geometry, target);
  }
  updateHealpixTexture(batch, target);
  updateMeshProjectionUniforms();
}

type THealpixTexture = Pick<
  THealpixBatch,
  "batchIndex" | "dataValues" | "width" | "height" | "dataRect"
>;

function updateHealpixTexture(batch: THealpixTexture, target = mainMeshes) {
  const mesh = target[batch.batchIndex];
  if (!mesh) {
    return;
  }
  mesh.userData.dataRect = batch.dataRect;
  const material = mesh.material as THREE.ShaderMaterial;
  material.uniforms.data.value.dispose();
  const texture = new THREE.DataTexture(
    batch.dataValues,
    batch.width,
    batch.height,
    THREE.RedFormat,
    THREE.FloatType,
    THREE.UVMapping
  );
  texture.needsUpdate = true;
  material.uniforms.data.value = texture;
  material.uniforms.dataUvOffset.value.set(batch.dataRect.u, batch.dataRect.v);
  material.uniforms.dataUvScale.value.set(
    batch.dataRect.width,
    batch.dataRect.height
  );
  // Only crop to the rect when it's a genuine sub-region of the face (a
  // regional/sparse dataset); a full face has nothing to discard around.
  material.uniforms.clipToDataRect.value =
    batch.dataRect.width < 1 || batch.dataRect.height < 1 ? 1 : 0;
}

// eslint-disable-next-line max-lines-per-function
async function processHealpixChunks(
  level: TReadLevel,
  indices: (number | zarr.Slice | null)[],
  isCurrent: () => boolean,
  options: {
    /** The cell coordinate of a level loaded whole, when it is sparse. */
    cells?: number[];
    deferDisplay: boolean;
    /** The blocks to read of a level loaded by view, over the parts kept. */
    window?: { blocks: number[]; seed: TFaceParts };
    target?: Array<THREE.Mesh | undefined>;
  }
) {
  const { datavar, grid, sources } = level;
  const { cells, deferDisplay, window, target = mainMeshes } = options;
  let dataMin = Number.POSITIVE_INFINITY;
  let dataMax = Number.NEGATIVE_INFINITY;
  const histogramSummaries: THistogramSummary[] = [];
  const textures: THealpixTexture[] = [];
  const batches: THealpixBatch[] = [];
  const drawn: THealpixBatch[] = [];
  const helper = projectionHelper.value;
  const build = {
    grid: {
      scheme: grid.scheme,
      level: grid.level,
      ellipsoid: {
        // eslint-disable-next-line camelcase
        semi_major_axis: grid.semiMajorAxis,
        // eslint-disable-next-line camelcase
        semi_minor_axis: grid.semiMajorAxis * (1 - grid.flattening),
      },
    },
    attributes: datavar.attrs,
    ...getHealpixMissingAndFillValues(datavar),
    projectionType: helper.type,
    projectionCenter: { lat: helper.center.lat, lon: helper.center.lon },
  };
  // Streamlines want the complete replacement; a level or window swap
  // still shows the new window as it lands.
  const deferDisplayWhole = deferDisplay && store.isStreamlineLayerEnabled();
  if (!deferDisplay && target === mainMeshes) {
    clearHoverLookup();
  }
  // A window is read nearest the camera first and drawn as it lands, the
  // face under the camera first; with streamlines, the complete replacement
  // is staged.
  const ranges = window ? windowRanges(level, window.blocks) : undefined;
  const read: TWindowRead | undefined =
    ranges &&
    createWindowRead(
      fetchInOrder(ranges, (range) => fetchCellRange(level, range, indices), {
        isCurrent,
        // re-ranked as the camera moves: the cells still to read follow a pan
        rank: (range) =>
          rangeDistance(
            range,
            store.viewFootprint ?? { centreLon: 0, centreLat: 0 }
          ),
      }),
      ranges,
      window!.seed
    );
  const faces = [...(read?.pending.keys() ?? [])];
  for (let face = 0; face < HEALPIX_NUMCHUNKS; face++) {
    if (!faces.includes(face)) {
      faces.push(face);
    }
  }
  const shownFace = async (
    faceIndex: number,
    face: { data: Float32Array; cells?: number[] },
    readPercentile: (percentile: number) => Promise<Float32Array>
  ) =>
    // the level beneath is drawn as read
    sources === props.datasources
      ? await gapFill.fill(faceIndex, grid.nside, face, readPercentile)
      : { data: face.data, histogramSummary: undefined };
  // Read one face at a time: the face worker runs a single build at a time.
  for (const faceIndex of faces) {
    if (disposed || !isCurrent()) {
      return;
    }
    const range = getHealpixFaceRange(faceIndex, grid.nside, cells);
    const selection = indices.slice();
    selection[selection.length - 1] = zarr.slice(range.start, range.end);
    const readWhole = async (percentile?: number) => ({
      data:
        range.start === range.end
          ? new Float32Array()
          : castDataVarToFloat32(
              await fetchHealpixVariableData(
                selection,
                undefined,
                sources,
                percentile
              )
            ),
      cells: range.cells,
    });
    // a partial face is drawn as read: it is smoothed once it is in
    const drawPartial = async (partial: TRangeCells) => {
      const batch = await buildHealpixFace({
        ...build,
        faceIndex,
        cells: partial.cells,
        data: partial.data,
      });
      if (!disposed && isCurrent()) {
        updateHealpixBatch(batch, target);
        drawOverBackdrop();
      }
    };
    const face: {
      data: Float32Array;
      cells?: number[];
      ranges?: TAxisRange[];
    } = read
      ? await readFaceProgressively(
          faceIndex,
          read,
          deferDisplayWhole ? undefined : drawPartial,
          isCurrent
        )
      : await readWhole();
    if (disposed || !isCurrent()) {
      return;
    }
    const shown = await shownFace(faceIndex, face, async (percentile) =>
      face.ranges
        ? (
            await readRanges(
              face.ranges,
              (range) => fetchCellRange(level, range, indices, percentile),
              isCurrent
            )
          ).data
        : (await readWhole(percentile)).data
    );
    if (disposed || !isCurrent()) {
      return;
    }
    const batch = await buildHealpixFace({
      ...build,
      faceIndex,
      cells: face.cells,
      data: shown.data,
    });
    // filled cells are drawn, not counted
    const summary = shown.histogramSummary ?? batch.histogramSummary;
    histogramSummaries.push(summary);
    dataMin = dataMin > summary.min ? summary.min : dataMin;
    dataMax = dataMax < summary.max ? summary.max : dataMax;
    textures.push({
      batchIndex: batch.batchIndex,
      dataValues: batch.dataValues,
      width: batch.width,
      height: batch.height,
      dataRect: batch.dataRect,
    });
    drawn.push(batch);
    if (!isCurrent()) {
      return;
    }
    // a face drawn as it landed is complete now; one with no cells in the
    // window goes with the others
    if (deferDisplay && (deferDisplayWhole || !read || batch.width === 0)) {
      batches.push(batch);
    } else {
      updateHealpixBatch(batch, target);
    }
  }
  if (ranges && level.digest) {
    // the stream went through the working set nearest first, so the chunks
    // under the camera left it first: read them once more, last
    void readRanges(
      ranges.slice(0, CENTRE_RANGES),
      (range) => fetchCellRange(level, range, indices),
      isCurrent
    ).catch(() => undefined);
  }
  const parts: TFaceParts = new Map(
    [...(read?.assemblies ?? [])].map(([face, assembly]) => [
      face,
      assembly.parts(),
    ])
  );
  return {
    dataMin,
    dataMax,
    histogramSummaries,
    textures,
    batches,
    drawn,
    parts,
  };
}

/** Load the coarsest level at the selected slice whole, unless it is on screen. */
async function loadCoarsest(
  indices: (number | zarr.Slice | null)[],
  isCurrent: () => boolean
) {
  const sources = props.datasources!;
  const index = coarsestLevel(sources.levels);
  if (index === undefined || index === (sources.selectedLevel ?? 0)) {
    return undefined;
  }
  const key = JSON.stringify([
    index,
    varnameSelector.value,
    indices,
    digestFields.key(),
  ]);
  if (coarsestFrame?.key === key) {
    return coarsestFrame;
  }
  const coarse = { ...sources, selectedLevel: index };
  const datavar = await getDataVar(varnameSelector.value, coarse);
  if (!datavar) {
    return undefined;
  }
  const grid = await getHealpixGridParameters(coarse);
  const result = await processHealpixChunks(
    {
      sources: coarse,
      datavar,
      grid,
      chunkCells: datavar.chunks.at(-1)!,
      digest: digestVariableOf(datavar.attrs) !== undefined,
    },
    indices,
    isCurrent,
    { cells: await getCells(coarse), deferDisplay: true }
  );
  if (!result) {
    return undefined;
  }
  coarsestFrame = {
    key,
    index,
    grid,
    min: result.dataMin,
    max: result.dataMax,
    histogramSummaries: result.histogramSummaries,
    batches: result.batches,
  };
  return coarsestFrame;
}

/** Draw a level kept in memory beneath the one on screen, or nothing. */
function showBackdrop(beneath: TBeneath | undefined) {
  if (backdropKey === beneath?.key) {
    return;
  }
  backdropKey = beneath?.key;
  for (let face = 0; face < HEALPIX_NUMCHUNKS; face++) {
    disposeHealpixMesh(face, backdropMeshes);
  }
  beneath?.batches.forEach((batch) =>
    updateHealpixBatch(batch, backdropMeshes)
  );
  drawOverBackdrop();
}

/**
 * A level is drawn after the level beneath and over it whatever the depth
 * says: up close the two surfaces are nearer than float32 positions can
 * order.
 */
function drawOverBackdrop() {
  for (const mesh of mainMeshes) {
    if (mesh) {
      (mesh.material as THREE.ShaderMaterial).depthFunc = backdropKey
        ? THREE.AlwaysDepth
        : THREE.LessEqualDepth;
    }
  }
}

/**
 * The finest value drawn at a point: the level on screen where it has a
 * cell there, else the level beneath, named.
 */
function healpixHoverLookup(
  lat: number,
  lon: number
): TGridHoverLookupResult | null {
  let grid;
  try {
    grid = unpackGrid();
  } catch {
    return null;
  }
  const normalizedLon = ProjectionHelper.normalizeLongitude(lon);
  const own = faceValue(mainMeshes, grid, normalizedLon, lat);
  const beneath =
    own && own.value !== null ? undefined : fill.lookup(normalizedLon, lat);
  const hit = beneath ?? own;
  if (!hit) {
    return null;
  }
  return {
    lat: hit.lat,
    lon: hit.lon,
    value: hit.value,
    interpolated:
      hit === own && gapFill.interpolated(own.pixel, grid) !== undefined,
    level: beneath?.level,
    status:
      hit.value === null
        ? HOVERED_GRID_POINT_STATUS.MISSING
        : HOVERED_GRID_POINT_STATUS.VALUE,
  };
}

// eslint-disable-next-line max-lines-per-function
async function fetchAndRenderData(
  datavar: zarr.Array<zarr.DataType, zarr.AsyncReadable>,
  isCurrent: () => boolean,
  stageDisplay: boolean
) {
  const deferDisplay = store.isStreamlineLayerEnabled() || stageDisplay;
  const level = readLevel(datavar);
  const { grid } = level;
  const selected = props.datasources!.selectedLevel ?? 0;

  // A level loaded by view never reads its whole `cell` coordinate.
  const cellCoord = loadedBlocks ? undefined : await getCells();
  fitCameraToCells(grid, cellCoord);
  const { dimensionRanges, indices } = await prepareDimensionData(datavar);
  // One after the other: the face worker runs a single build at a time.
  // The coarsest level sets the range; the parent is drawn beneath before
  // the level's own blocks stream in over it.
  const coarsest = await loadCoarsest(indices, isCurrent).catch(
    () => undefined
  );
  if (
    coarsest &&
    store.levelRangeShared &&
    store.selection.low === store.selection.high
  ) {
    // the first load, with no range yet: the faces drawn as they land take
    // the shared range now rather than one colour until the frame commits
    store.updateBounds({ low: coarsest.min, high: coarsest.max });
  }
  const key = JSON.stringify([
    varnameSelector.value,
    indices,
    digestFields.key(),
  ]);
  lastField = { indices, key };
  // a sparse level loaded whole leaves the rest of the globe to the coarsest
  const beneath = loadedBlocks
    ? await fill.loadBeneath(indices, key, isCurrent, coarsest)
    : levelCellCount < 12 * grid.nside ** 2
      ? fill.coarsestBeneath(coarsest)
      : undefined;
  if (!isCurrent()) {
    return;
  }
  const own = loadedBlocks && fill.take(selected, key, loadedBlocks);
  // the textures of the level and its parent only, from before the level's
  // read; the blocks of other levels stay cached within the bound
  fill.retain([selected, beneath?.index]);
  const result = await processHealpixChunks(level, indices, isCurrent, {
    cells: cellCoord,
    deferDisplay,
    window: own ? { blocks: own.missing, seed: own.seed } : undefined,
  });
  if (!result || !isCurrent()) {
    return;
  }
  if (loadedBlocks) {
    fill.keep(selected, key, level, result.parts, result.drawn);
  }
  const { dataMin, dataMax, histogramSummaries, textures, batches } = result;

  lastStreamlineContext = { indices, grid, cellCoord };
  volume.setContext(
    loadedBlocks
      ? undefined
      : {
          dimensionNames: selectedDimensionNames.value,
          indices,
          grid: {
            kind: VOLUME_GRID_TYPES.HEALPIX,
            nside: grid.nside,
            cellCoordinates: cellCoord
              ? Float64Array.from(cellCoord)
              : undefined,
            options: {
              scheme: grid.scheme,
              level: grid.level,
              ellipsoid: {
                // eslint-disable-next-line camelcase
                semi_major_axis: grid.semiMajorAxis,
                // eslint-disable-next-line camelcase
                semi_minor_axis: grid.semiMajorAxis * (1 - grid.flattening),
              },
            },
          },
        }
  );

  if (!isCurrent()) {
    return;
  }
  const dimInfo = await getDimensionValues(dimensionRanges, indices);

  // the coarsest level sets the range of every level, unless each level is
  // to take its own
  const rangeSource = store.levelRangeShared ? coarsest : undefined;
  const scalarInfo = {
    attrs: datavar.attrs,
    dimInfo,
    bounds: {
      low: rangeSource?.min ?? dataMin,
      high: rangeSource?.max ?? dataMax,
    },
    dimRanges: dimensionRanges,
  };
  let geometryPending = deferDisplay;
  const renderScalar = () => {
    showBackdrop(beneath);
    if (geometryPending) {
      batches.forEach((batch) => updateHealpixBatch(batch));
      batches.length = 0;
      geometryPending = false;
    } else {
      textures.forEach((texture) => updateHealpixTexture(texture));
    }
    drawOverBackdrop();
    setHoverLookup(healpixHoverLookup);
  };
  if (!isCurrent()) {
    return;
  }
  scalarCache.captureScalar({
    render: renderScalar,
    info: scalarInfo,
    indices: indices as number[],
    data: rangeSource?.histogramSummaries ?? histogramSummaries,
    isCurrent,
  });
  await updateStreamlines(lastStreamlineContext);
  if (isCurrent() && !store.streamlineMagnitudeDisplayed) {
    await scalarCache.restoreScalar();
  }
}

onBeforeMount(async () => {
  await datasourceUpdate();
  gridPrepared.value = true;
});

onBeforeUnmount(() => {
  disposed = true;
  // the notice is this grid's: the next dataset starts without it
  store.setViewWindowClipped(false);
  streamlineRequestRevision++;
  terminateHealpixWorker();
  terminateGridDataWorker();
  for (let ipix = 0; ipix < HEALPIX_NUMCHUNKS; ++ipix) {
    disposeHealpixMesh(ipix);
    disposeHealpixMesh(ipix, backdropMeshes);
  }
});

defineExpose({
  makeSnapshot,
  toggleRotate,
  applyCameraPreset,
});
</script>

<template>
  <div ref="box" class="globe_box" tabindex="0" autofocus>
    <canvas ref="canvas" class="globe_canvas"> </canvas>
  </div>
</template>
